import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Paper,
  IconButton,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Collapse,
  Divider,
  CircularProgress,
  Avatar,
} from '@mui/material';
import {
  CloudUpload,
  Close,
  Download,
  FileUpload,
  Check,
  Error,
  Warning,
  Visibility,
  ExpandMore,
  ExpandLess,
  Delete,
  Assignment,
  Preview,
  TableChart,
  DirectionsCar,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

interface BulkVehicleUploadProps {
  open: boolean;
  onClose: () => void;
  onUpload: (vehicles: any[]) => Promise<void>;
}

interface UploadedVehicle {
  id: string;
  make: string;
  model: string;
  year: number;
  licensePlate: string;
  type: string;
  dailyRate: number;
  seats: number;
  mileage: number;
  status: 'valid' | 'warning' | 'error';
  errors: string[];
  warnings: string[];
}

const steps = ['Upload File', 'Validate Data', 'Review & Import'];

const requiredColumns = [
  'make', 'model', 'year', 'licensePlate', 'type', 'dailyRate', 'seats', 'mileage'
];

const optionalColumns = [
  'color', 'vin', 'description', 'features', 'engine', 'transmission', 'fuelType'
];

export default function BulkVehicleUploadMUI({ open, onClose, onUpload }: BulkVehicleUploadProps) {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [activeStep, setActiveStep] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<UploadedVehicle[]>([]);
  const [validationSummary, setValidationSummary] = useState({
    total: 0,
    valid: 0,
    warnings: 0,
    errors: 0
  });
  const [showDetails, setShowDetails] = useState(false);
  const [previewExpanded, setPreviewExpanded] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleFileUpload = (files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }
    
    const file = files[0];
    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('Please select a CSV file');
      return;
    }

    setUploadedFile(file);
    setErrorMessage(''); // Clear any previous error messages
    // Don't auto-parse, let user click Next button
  };

  const parseCSVFile = async (file: File) => {
    setIsProcessing(true);
    
    try {
      
      const text = await file.text();
      
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        throw new Error('CSV file must contain headers and at least one data row');
      }

      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      
      const dataLines = lines.slice(1);

      
      const missingColumns = [];
      for (const col of requiredColumns) {
        if (!headers.includes(col)) {
          missingColumns.push(col);
        }
      }
      if (missingColumns.length > 0) {
        // Avoid join() method - use manual concatenation
        let missingList = '';
        for (let i = 0; i < missingColumns.length; i++) {
          missingList += missingColumns[i];
          if (i < missingColumns.length - 1) missingList += ', ';
        }
        
        let requiredList = '';
        for (let i = 0; i < requiredColumns.length; i++) {
          requiredList += requiredColumns[i];
          if (i < requiredColumns.length - 1) requiredList += ', ';
        }
        
        let headersList = '';
        for (let i = 0; i < headers.length; i++) {
          headersList += headers[i];
          if (i < headers.length - 1) headersList += ', ';
        }
        
        const userMessage = `This CSV file doesn't appear to be a vehicle file. 

Required columns for vehicles: ${requiredList}

Your file has: ${headersList}

Please download the vehicle template or use a proper vehicle CSV file.`;
        throw new Error(userMessage);
      }

      const vehicles: UploadedVehicle[] = [];
      
      for (let i = 0; i < dataLines.length; i++) {
        const line = dataLines[i];
        const values = line.split(',').map(field => field.trim().replace(/"/g, ''));
        
        const vehicleData: any = {};
        headers.forEach((header, headerIndex) => {
          vehicleData[header] = values[headerIndex] || '';
        });

        const validatedVehicle = validateVehicleData(vehicleData, i + 2);
        
        vehicles.push(validatedVehicle);
      }

      setParsedData(vehicles);
      
      const summary = {
        total: vehicles.length,
        valid: vehicles.filter(v => v.status === 'valid').length,
        warnings: vehicles.filter(v => v.status === 'warning').length,
        errors: vehicles.filter(v => v.status === 'error').length
      };
      setValidationSummary(summary);

      setActiveStep(1);
    } catch (error) {
      console.error('CSV parsing failed:', error);
      // Show user-friendly error message
      setUploadedFile(null); // Clear the uploaded file
      setErrorMessage(error.message); // Set error message for UI display
    } finally {
      setIsProcessing(false);
    }
  };

  const validateVehicleData = (data: any, lineNumber: number): UploadedVehicle => {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Simple validation without constructor calls
    const make = String(data.make || '').trim();
    const model = String(data.model || '').trim();
    const licensePlate = String(data.licensePlate || '').trim();
    const type = String(data.type || '').trim();
    
    // Basic required field checks
    if (!make) errors.push('Make is required');
    if (!model) errors.push('Model is required');
    if (!licensePlate) errors.push('License plate is required');
    if (!type) errors.push('Vehicle type is required');
    
    // Parse numbers safely
    const yearStr = String(data.year || '');
    const year = yearStr ? parseInt(yearStr, 10) : 0;
    if (!yearStr || isNaN(year) || year < 1900 || year > 2026) {
      errors.push('Valid year is required');
    }
    
    const dailyRateStr = String(data.dailyRate || '');
    const dailyRate = dailyRateStr ? parseFloat(dailyRateStr) : 0;
    if (!dailyRateStr || isNaN(dailyRate) || dailyRate <= 0) {
      errors.push('Valid daily rate is required');
    }
    
    const seatsStr = String(data.seats || '');
    const seats = seatsStr ? parseInt(seatsStr, 10) : 0;
    if (!seatsStr || isNaN(seats) || seats <= 0 || seats > 15) {
      errors.push('Valid seats count is required');
    }
    
    const mileageStr = String(data.mileage || '');
    const mileage = mileageStr ? parseInt(mileageStr, 10) : 0;
    if (!mileageStr || isNaN(mileage) || mileage < 0) {
      errors.push('Valid mileage is required');
    }

    const status = errors.length > 0 ? 'error' : warnings.length > 0 ? 'warning' : 'valid';

    return {
      id: `row-${lineNumber}`,
      make,
      model,
      year,
      licensePlate,
      type,
      dailyRate,
      seats,
      mileage,
      status,
      errors,
      warnings
    };
  };

  const handleNext = () => {
    if (activeStep === 1 && validationSummary.errors === 0) {
      setActiveStep(2);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleImport = async () => {
    setIsProcessing(true);
    try {
      const validVehicles = parsedData.filter(v => v.status !== 'error');
      await onUpload(validVehicles);
      handleClose();
    } catch (error) {
      console.error('Failed to import vehicles:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setUploadedFile(null);
    setParsedData([]);
    setValidationSummary({ total: 0, valid: 0, warnings: 0, errors: 0 });
    setShowDetails(false);
    setPreviewExpanded([]);
    onClose();
  };

  const togglePreviewExpanded = (id: string) => {
    setPreviewExpanded(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const getStatusChip = (status: 'valid' | 'warning' | 'error') => {
    const config = {
      valid: { label: 'Valid', color: 'success' as const, icon: <Check /> },
      warning: { label: 'Warning', color: 'warning' as const, icon: <Warning /> },
      error: { label: 'Error', color: 'error' as const, icon: <Error /> }
    };
    
    const { label, color, icon } = config[status];
    return <Chip label={label} color={color} size="small" icon={icon} />;
  };

  const downloadTemplate = () => {
    // Avoid join() - manually build CSV
    let headerLine = '';
    const allColumns = [...requiredColumns, ...optionalColumns];
    for (let i = 0; i < allColumns.length; i++) {
      headerLine += allColumns[i];
      if (i < allColumns.length - 1) headerLine += ',';
    }
    
    const sampleData = 'Toyota,Camry,2022,ABC-123,sedan,89.99,5,25000,White,1HGBH41JXMN109186,Well-maintained sedan,Bluetooth GPS,2.5L 4-Cylinder,Automatic,Gasoline';
    
    const csvContent = headerLine + '\n' + sampleData;
    
    // Create download link without Blob constructor
    const dataUri = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent);
    const link = document.createElement('a');
    link.href = dataUri;
    link.download = 'vehicle_upload_template.csv';
    link.click();
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Download />
                      Download Template
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Download the CSV template with proper column headers and sample data.
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={downloadTemplate}
                      startIcon={<Download />}
                      fullWidth
                    >
                      Download Template
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Assignment />
                      Required Columns
                    </Typography>
                    <List dense>
                      {requiredColumns.map(col => (
                        <ListItem key={col}>
                          <ListItemIcon>
                            <Check color="success" />
                          </ListItemIcon>
                          <ListItemText primary={col} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={(e) => {
                handleFileUpload(e.target.files);
              }}
              style={{ display: 'none' }}
            />

            <Paper
              sx={{
                p: 6,
                textAlign: 'center',
                border: '2px dashed',
                borderColor: uploadedFile ? 'success.main' : 'divider',
                mt: 3,
                cursor: 'pointer',
                bgcolor: uploadedFile ? 'success.main' + '08' : 'transparent',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover'
                }
              }}
              onClick={() => {
                fileInputRef.current?.click();
              }}
            >
              {uploadedFile ? (
                <>
                  <Check sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                  <Typography variant="h6" sx={{ mb: 1, color: 'success.main' }}>
                    File uploaded successfully!
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {uploadedFile.name}
                  </Typography>
                </>
              ) : (
                <>
                  <CloudUpload sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    Click to upload CSV file
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Maximum file size: 10MB
                  </Typography>
                </>
              )}
            </Paper>

            {errorMessage && (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setErrorMessage('')}>
                <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                  {errorMessage}
                </Typography>
              </Alert>
            )}

            {isProcessing && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                  Processing file...
                </Typography>
              </Box>
            )}
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            {/* Validation Summary */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 1 }}>
                      <TableChart />
                    </Avatar>
                    <Typography variant="h4">{validationSummary.total}</Typography>
                    <Typography variant="body2" color="text.secondary">Total Rows</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Avatar sx={{ bgcolor: 'success.main', mx: 'auto', mb: 1 }}>
                      <Check />
                    </Avatar>
                    <Typography variant="h4">{validationSummary.valid}</Typography>
                    <Typography variant="body2" color="text.secondary">Valid</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Avatar sx={{ bgcolor: 'warning.main', mx: 'auto', mb: 1 }}>
                      <Warning />
                    </Avatar>
                    <Typography variant="h4">{validationSummary.warnings}</Typography>
                    <Typography variant="body2" color="text.secondary">Warnings</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Avatar sx={{ bgcolor: 'error.main', mx: 'auto', mb: 1 }}>
                      <Error />
                    </Avatar>
                    <Typography variant="h4">{validationSummary.errors}</Typography>
                    <Typography variant="body2" color="text.secondary">Errors</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {validationSummary.errors > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {validationSummary.errors} row(s) have errors that must be fixed before importing.
                Please correct the data in your CSV file and re-upload.
              </Alert>
            )}

            {validationSummary.warnings > 0 && validationSummary.errors === 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {validationSummary.warnings} row(s) have warnings. You can proceed with import, 
                but please review the warnings.
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Validation Results</Typography>
              <Button
                variant="outlined"
                startIcon={showDetails ? <ExpandLess /> : <ExpandMore />}
                onClick={() => setShowDetails(!showDetails)}
              >
                {showDetails ? 'Hide' : 'Show'} Details
              </Button>
            </Box>

            <Collapse in={showDetails}>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Vehicle</TableCell>
                      <TableCell>License Plate</TableCell>
                      <TableCell>Daily Rate</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Issues</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsedData.slice(0, 10).map((vehicle) => (
                      <TableRow key={vehicle.id}>
                        <TableCell>{vehicle.id.split('-')[1]}</TableCell>
                        <TableCell>
                          {vehicle.year} {vehicle.make} {vehicle.model}
                        </TableCell>
                        <TableCell>{vehicle.licensePlate}</TableCell>
                        <TableCell>${vehicle.dailyRate}</TableCell>
                        <TableCell>{getStatusChip(vehicle.status)}</TableCell>
                        <TableCell>
                          {vehicle.errors.length + vehicle.warnings.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {vehicle.errors.length} errors, {vehicle.warnings.length} warnings
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {parsedData.length > 10 && (
                <Typography variant="caption" color="text.secondary" sx={{ p: 2, display: 'block' }}>
                  Showing first 10 rows. {parsedData.length - 10} more rows...
                </Typography>
              )}
            </Collapse>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Ready to import {validationSummary.valid + validationSummary.warnings} vehicles.
              {validationSummary.errors > 0 && ` ${validationSummary.errors} rows with errors will be skipped.`}
            </Alert>

            <Typography variant="h6" sx={{ mb: 2 }}>Import Summary</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <DirectionsCar sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h6">{validationSummary.valid + validationSummary.warnings}</Typography>
                  <Typography variant="body2" color="text.secondary">Vehicles to Import</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Check sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
                  <Typography variant="h6">{validationSummary.valid}</Typography>
                  <Typography variant="body2" color="text.secondary">No Issues</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Warning sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
                  <Typography variant="h6">{validationSummary.warnings}</Typography>
                  <Typography variant="body2" color="text.secondary">With Warnings</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <FileUpload />
          </Avatar>
          <Typography variant="h6">Bulk Vehicle Upload</Typography>
        </Box>
        <IconButton onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <Box sx={{ px: 3, py: 1 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ flex: 1, overflow: 'auto' }}>
        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={handleClose} disabled={isProcessing}>
          Cancel
        </Button>
        
        <Box sx={{ flex: 1 }} />
        
        
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={isProcessing}>
            Back
          </Button>
        )}
        
        {activeStep === 0 && (
          <Button
            variant="contained"
            onClick={() => {
              if (uploadedFile) {
                parseCSVFile(uploadedFile);
              } else {
                alert('Please upload a CSV file first');
              }
            }}
            disabled={isProcessing || !uploadedFile}
          >
            Next
          </Button>
        )}
        
        {activeStep === 1 && (
          <Button
            variant="contained"
            onClick={() => {
              handleNext();
            }}
            disabled={validationSummary.errors > 0 || isProcessing}
          >
            Continue to Import
          </Button>
        )}
        
        {activeStep === 2 && (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={isProcessing}
            startIcon={isProcessing ? <CircularProgress size={20} /> : <FileUpload />}
          >
            {isProcessing ? 'Importing...' : 'Import Vehicles'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}